import { useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { Drawer, DrawerContent, DrawerTitle } from "./ui/drawer";
import { LEVELS, AVAILABILITY, CATEGORIES } from "../constants/rox";
import { useT, useLabels } from "../i18n";

export const DEFAULT_FILTERS = { race: "all", city: "all", category: "all", level: "all", time: "all", availability: "all", distance: "all", sort: "score" };

export const SORTS = [
  { id: "score", key: "sort_score" },
  { id: "race_date", key: "sort_race_date" },
  { id: "city", key: "sort_city" },
];
const TIMES = [{ id: "5", label: "± 5 min" }, { id: "10", label: "± 10 min" }, { id: "20", label: "± 20 min" }];
const DISTANCES = [{ id: "50", label: "< 50 km" }, { id: "150", label: "< 150 km" }, { id: "300", label: "< 300 km" }];

export const countActive = (f) => Object.entries(f).filter(([k, v]) => k !== "sort" && v !== "all").length;

export const applyFilters = (pairs, f, me) =>
  pairs.filter(
    (p) =>
      (f.race === "all" || p.race === f.race) &&
      (f.city === "all" || p.city === f.city) &&
      (f.category === "all" || p.category === f.category) &&
      (f.level === "all" || p.level === f.level) &&
      (f.time === "all" || Math.abs(p.target_time - (me?.target_time ?? p.target_time)) <= Number(f.time)) &&
      (f.availability === "all" || p.availability.includes(f.availability)) &&
      (f.distance === "all" || (p.distance_km !== null && p.distance_km <= Number(f.distance))),
  );

export const sortPairs = (pairs, sort, races, myCity) => {
  const dates = Object.fromEntries(races.map((r) => [r.id, r.date ? new Date(r.date).getTime() : Infinity]));
  const byScore = (a, b) => b.score - a.score;
  const list = [...pairs];
  if (sort === "race_date") list.sort((a, b) => (dates[a.race] ?? Infinity) - (dates[b.race] ?? Infinity) || byScore(a, b));
  else if (sort === "city") {
    const same = (p) => (p.city.trim().toLowerCase() === (myCity || "").trim().toLowerCase() ? 0 : 1);
    list.sort((a, b) => same(a) - same(b) || byScore(a, b));
  } else list.sort(byScore);
  return list;
};

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    data-active={active}
    onClick={onClick}
    className={`shrink-0 rounded-[3px] border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] transition-[background-color,border-color,color] ${
      active ? "border-rox-off bg-rox-off text-rox-black" : "border-rox-line text-rox-off/80 hover:border-rox-off/50"
    }`}
  >
    {children}
  </button>
);

const Row = ({ label, options, value, onChange, prefix, allLabel = "Tous" }) => (
  <div>
    <p className="eyebrow mb-2">{label}</p>
    <div className="flex flex-wrap gap-2">
      <Chip active={value === "all"} onClick={() => onChange("all")} testId={`filter-${prefix}-all`}>{allLabel}</Chip>
      {options.map((o) => (
        <Chip key={o.id} active={value === o.id} onClick={() => onChange(value === o.id ? "all" : o.id)} testId={`filter-${prefix}-${o.id}`}>
          {o.label}
        </Chip>
      ))}
    </div>
  </div>
);

export const FiltersDrawer = ({ races, cities, filters, onApply }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(filters);
  const { t } = useT();
  const L = useLabels();
  const set = (k) => (v) => setDraft({ ...draft, [k]: v });
  const active = countActive(filters);

  return (
    <Drawer open={open} onOpenChange={(o) => { setOpen(o); if (o) setDraft(filters); }} shouldScaleBackground={false}>
      <button data-testid="open-filters" onClick={() => setOpen(true)} className="mt-5 flex w-full items-center justify-between rounded-[4px] border border-rox-line bg-rox-panel px-4 py-3 font-display text-sm uppercase tracking-wider transition-[border-color] hover:border-rox-off/50">
        <span className="flex items-center gap-2"><SlidersHorizontal size={16} /> {t("filters")}</span>
        {active > 0 && <span data-testid="filters-active-count" className="rounded-sm bg-rox-orange px-2 py-0.5 text-xs text-rox-black">{active}</span>}
      </button>
      <DrawerContent className="mx-auto max-w-md rounded-t-[8px] lg:max-w-2xl border-rox-line bg-rox-black text-rox-off" data-testid="filters-drawer">
        <DrawerTitle className="h-display px-5 pt-3 text-3xl">{t("filters_title")}</DrawerTitle>
        <div className="max-h-[60vh] space-y-5 overflow-y-auto px-5 py-5">
          <Row label={t("sort_by")} prefix="sort" options={SORTS.filter((s) => s.id !== "score").map((s) => ({ id: s.id, label: t(s.key) }))} value={draft.sort === "score" ? "all" : draft.sort} onChange={(v) => set("sort")(v === "all" ? "score" : v)} allLabel={t("sort_score")} />
          <Row label={t("race")} prefix="race" options={races.map((r) => ({ id: r.id, label: r.name.replace(/^.*HYROX\s*/i, "") || r.name }))} value={draft.race} onChange={set("race")} allLabel={t("all_f")} />
          <Row label={t("city")} prefix="city" options={cities.map((c) => ({ id: c, label: c }))} value={draft.city} onChange={set("city")} allLabel={t("all_f")} />
          <Row label={t("format")} prefix="category" options={CATEGORIES.map((c) => ({ id: c.id, label: L.categoryShort(c.id) }))} value={draft.category} onChange={set("category")} allLabel={t("all_m")} />
          <Row label={t("level")} prefix="level" options={LEVELS.map((l) => ({ id: l.id, label: L.level({ level: l.id }) }))} value={draft.level} onChange={set("level")} allLabel={t("all_m")} />
          <Row label={t("target_time")} prefix="time" options={TIMES} value={draft.time} onChange={set("time")} allLabel={t("all_m")} />
          <Row label={t("availability")} prefix="availability" options={AVAILABILITY.map((a) => ({ id: a.id, label: L.availabilityLabel(a.id) }))} value={draft.availability} onChange={set("availability")} allLabel={t("all_f")} />
          <Row label={t("distance")} prefix="distance" options={DISTANCES} value={draft.distance} onChange={set("distance")} allLabel={t("all_f")} />
        </div>
        <div className="grid grid-cols-2 gap-3 border-t border-rox-line px-5 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <button data-testid="filters-reset" className="btn-ghost py-3 text-base" onClick={() => { onApply(DEFAULT_FILTERS); setOpen(false); }}>{t("reset")}</button>
          <button data-testid="filters-apply" className="btn-primary py-3 text-base" onClick={() => { onApply(draft); setOpen(false); }}>{t("apply")}</button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
